import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { site } from '@/mocks/site';

const navItems = [
  { name: '首页', path: '/' },
  { name: '内幕观察', path: '/insight' },
  { name: '常见问题', path: '/faq' },
  { name: '关于我们', path: '/about' },
  { name: '联系我们', path: '/contact' },
];

export default function SiteHeader() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(event.target as Node)) {
        setCatOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const isCategoryActive = location.pathname.startsWith('/category');

  return (
    <header className="sticky top-0 z-50 w-full">
      <div className="bg-primary-700">
        <div className="mx-auto flex max-w-content items-center justify-between gap-4 px-4 py-2 md:px-6">
          <p className="truncate font-label text-xs text-primary-50">
            坚持公开信息核对，不接受企业付费删帖
          </p>
          <a
            href={`mailto:${site.email}`}
            className="hidden shrink-0 font-label text-xs text-primary-100 transition-colors hover:text-background-50 sm:block"
          >
            {site.email}
          </a>
        </div>
      </div>

      <div
        className={`w-full border-b bg-background-50/95 backdrop-blur transition-colors ${
          scrolled ? 'border-background-300' : 'border-background-200'
        }`}
      >
        <div className="mx-auto flex h-16 max-w-content items-center justify-between gap-6 px-4 md:h-[72px] md:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-600 font-heading text-lg font-black text-background-50">
              镜
            </span>
            <span className="leading-none">
              <span className="block font-heading text-lg font-bold tracking-tight text-foreground-950">
                {site.name}
              </span>
              <span className="mt-0.5 hidden font-label text-[10px] tracking-[0.24em] text-foreground-500 sm:block">
                {site.nameEn}
              </span>
            </span>
          </Link>

          <nav aria-label="主导航" className="hidden items-center gap-1 lg:flex">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`whitespace-nowrap rounded-md px-3.5 py-2 font-label text-sm transition-colors ${
                  isActive(item.path)
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                }`}
              >
                {item.name}
              </Link>
            ))}
            <div className="relative" ref={catRef}>
              <button
                type="button"
                onClick={() => setCatOpen((prev) => !prev)}
                aria-expanded={catOpen}
                className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3.5 py-2 font-label text-sm transition-colors ${
                  isCategoryActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                }`}
              >
                主题分类
                <i className={`ri-arrow-down-s-line text-base transition-transform ${catOpen ? 'rotate-180' : ''}`} />
              </button>
              {catOpen && (
                <div className="absolute right-0 top-full z-50 mt-2 w-64 rounded-lg border border-background-200 bg-background-50 p-2">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-start gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-background-100"
                    >
                      <i className={`${cat.icon} mt-0.5 text-lg text-primary-600`} />
                      <span>
                        <span className="block font-label text-sm font-medium text-foreground-900">
                          {cat.name}
                        </span>
                        <span className="mt-0.5 block font-label text-xs leading-relaxed text-foreground-500">
                          {cat.short}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/contact"
              className="hidden whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-700 sm:block"
            >
              我要投稿
            </Link>
            <button
              type="button"
              onClick={() => setMobileOpen((prev) => !prev)}
              aria-label="打开菜单"
              aria-expanded={mobileOpen}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-b border-background-200 bg-background-50 lg:hidden">
          <div className="mx-auto max-w-content px-4 py-4 md:px-6">
            <nav aria-label="移动端导航" className="flex flex-col">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`rounded-md px-3 py-2.5 font-label text-sm ${
                    isActive(item.path) ? 'bg-primary-50 text-primary-700' : 'text-foreground-800'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
            <div className="mt-3 border-t border-background-200 pt-3">
              <p className="mb-2 px-3 font-label text-xs tracking-[0.24em] text-foreground-500">主题分类</p>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-2 rounded-md bg-background-100 px-3 py-2.5 font-label text-sm text-foreground-800"
                  >
                    <i className={`${cat.icon} text-base text-primary-600`} />
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
            <Link
              to="/contact"
              className="mt-4 block rounded-md bg-primary-600 px-4 py-3 text-center font-label text-sm text-background-50"
            >
              我要投稿
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}